/**
 * Shared recovery for stale dynamically-imported chunks — the failure mode seen
 * after a deploy, when a chunk hash no longer exists on the server and the SPA
 * fallback returns index.html ("Failed to fetch dynamically imported module" /
 * "Expected a JavaScript module but the server responded with text/html").
 *
 * A plain reload is NOT enough: the app ships a service worker that precaches
 * the app shell, so a reload can be served the SAME stale index.html and loop.
 * Recovery therefore unregisters service workers and clears caches first, then
 * cache-bust reloads — so the reload fetches the current shell + chunks.
 *
 * Guarded to ONE reload per stale-chunk event via sessionStorage, so a
 * genuinely-unrecoverable failure surfaces the error instead of reload-looping.
 * The guard is cleared on any successful lazy load (see lazyRetry), re-arming
 * recovery for the next deploy.
 */

const RELOAD_KEY = 'chunk_reload';

function safeGet(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function safeSet(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch { /* storage unavailable */ }
}

/** Clear the one-shot guard so the next stale-chunk event can reload again. */
export function clearChunkReloadGuard(): void {
  try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* storage unavailable */ }
}

/**
 * Attempt recovery from a stale chunk. Returns true if a reload was initiated
 * (caller should stop / not resolve), false if recovery was already attempted
 * this cycle (caller should surface the error).
 */
export async function recoverFromStaleChunk(): Promise<boolean> {
  if (safeGet(RELOAD_KEY)) return false;
  safeSet(RELOAD_KEY, '1');

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* best-effort — reload regardless */ }

  const url = new URL(window.location.href);
  url.searchParams.set('_cb', Date.now().toString());
  window.location.replace(url.toString());
  return true;
}
